import { Controller } from "@hotwired/stimulus"
import { apiJson, formatApiErrorDetail } from "../lib/api_errore"

// ResultsStep di pages/TrovaBando.js (31a5bd1) con SceltaBando e CreaProgetto. Ogni «Cerca i bandi» (evento
// trova:profilo-confermato) rimonta il passo come l'originale: due frame nuovi con l'attesa, le opportunità dalle fonti
// `trova-opportunita-<n>` (POST del corpo di /opportunita/progetto a /app/trova/opportunita) e il catalogo
// `trova-catalogo-<n>` (POST del profilo confermato a /app/trova/risultati), indipendenti. I frame di una ricerca
// precedente escono dalla pagina, quindi le loro risposte non arrivano mai. Qui vivono anche lo stato dei risultati: i bandi spuntati per il
// progetto nuovo (in ordine di spunta), i bandi scelti del progetto riletti dal server (all'apertura, dopo ogni azione,
// al ritorno nella scheda) e i conteggi dei gruppi dopo i filtri del catalogo.
let sequenza = 0

export default class extends Controller {
  static targets = ["opportunita", "catalogo", "attesa", "attesaOpportunita", "modulo", "moduloOpportunita", "moduli", "schede", "gruppo", "tipo", "scegli", "spunta",
    "contoSpuntati", "approfondisciRiga", "approfondisci", "nome", "crea"]

  connect() {
    this.torna = () => { if (document.visibilityState !== "hidden") this.ricaricaScelti() }
    window.addEventListener("focus", this.torna)
    document.addEventListener("visibilitychange", this.torna)
  }

  disconnect() {
    window.removeEventListener("focus", this.torna)
    document.removeEventListener("visibilitychange", this.torna)
  }

  // ---- montaggio del passo -----------------------------------------------------------------------------------------

  avvia(event) {
    this.smonta()
    this.profile = event.detail.profile
    this.estratto = event.detail.estratto ?? null
    this.spuntati = []
    this.scelti = []
    this.busy = false
    sequenza += 1
    // le opportunità dalle fonti partono subito, nel loro frame, e non si ricaricano quando arriva il catalogo
    const fonti = this.attesaOpportunitaTarget.content.firstElementChild.cloneNode(true)
    const frameFonti = fonti.querySelector("turbo-frame")
    frameFonti.id = `trova-opportunita-${sequenza}`
    this.opportunitaTarget.replaceChildren(fonti)
    this.cercaFonti(frameFonti.id)
    const frame = this.attesaTarget.content.firstElementChild.cloneNode(true)
    frame.id = `trova-catalogo-${sequenza}`
    this.catalogoTarget.replaceChildren(frame)
    this.cercaCatalogo(frame.id)
    this.ricaricaScelti()
  }

  // il passo lasciato (Correggi profilo, Nuova analisi): il passo si smonta, nessuna risposta arriva più
  passo(event) {
    if (event.detail.step !== 2) this.smonta()
  }

  smonta() {
    // il passo smontato: niente più riletture dei bandi scelti (useBandiScelti smontato)
    this.profile = null
    this.moduliTarget.replaceChildren()
    this.opportunitaTarget.replaceChildren()
    this.catalogoTarget.replaceChildren()
    this.letturaScelti = null
  }

  // /finder/search con budgetStimato numerico (o null), come la ricerca dell'originale
  cercaCatalogo(frameId) {
    const payload = { ...this.profile, budgetStimato: this.profile.budgetStimato ? Number(this.profile.budgetStimato) : null }
    this.invia(this.moduloTarget, frameId, JSON.stringify(payload))
  }

  // ogni ricerca invia una copia del modulo, legata al suo frame: la risposta di una ricerca superata resta a quel
  // frame, uscito dalla pagina, e non tocca mai i frame della ricerca nuova
  invia(modulo, frameId, valore) {
    const copia = modulo.cloneNode(true)
    copia.querySelector("input[data-valore]").value = valore
    copia.dataset.turboFrame = frameId
    delete copia.dataset.trovaRisultatiTarget
    this.moduliTarget.append(copia)
    copia.requestSubmit()
  }

  // il corpo di POST /opportunita/progetto dal profilo confermato (Opportunita.js), con gli stessi valori di ripiego
  cercaFonti(frameId) {
    const p = this.profile
    const corpo = {
      atecoCodice: p.atecoCodice || "", regione: p.regione || "", provincia: p.provincia || "", comune: p.comuneNome || p.comune || "", settoriOpportunita: p.settoriOpportunita || [],
      obiettiviOpportunita: p.obiettiviOpportunita || [], costiOpportunita: p.costiOpportunita || [],
      beneficiariOpportunita: p.beneficiariOpportunita || [], formeOpportunita: p.formeOpportunita || [], statoOpportunita: p.statoOpportunita || "attuali",
      dimensione: p.dimensione || "", societaCostituita: p.societaCostituita ?? null,
      budgetStimato: p.budgetStimato ? Number(p.budgetStimato) : null,
    }
    this.invia(this.moduloOpportunitaTarget, frameId, JSON.stringify(corpo))
  }

  // ---- conteggi dei gruppi dopo i filtri ---------------------------------------------------------------------------

  visibili(el) {
    return [...el.querySelectorAll("[data-filtri-bando]")].filter((s) => !s.hidden).length
  }

  riconta() {
    this.tipoTargets.forEach((t) => {
      const n = this.visibili(t)
      t.hidden = n === 0
      t.querySelector("[data-conto]").textContent = String(n)
    })
    this.gruppoTargets.forEach((g) => {
      const n = this.visibili(g)
      g.querySelector("[data-conto]").textContent = String(n)
      if (g.dataset.nascondi) g.hidden = n === 0
      const vuoto = g.querySelector(":scope > [data-vuoto]")
      if (vuoto) vuoto.hidden = n > 0
    })
  }

  // ---- SceltaBando con un progetto ---------------------------------------------------------------------------------

  async ricaricaScelti() {
    const pid = this.profile?.project_id
    if (!pid) return
    const lettura = (this.letturaScelti = apiJson(`/api/projects/${encodeURIComponent(pid)}/bandi`))
    try {
      const data = await lettura
      if (lettura !== this.letturaScelti) return
      this.scelti = (data?.bandi || []).filter((b) => b.scelto).map((b) => String(b.id))
    } catch { /* the buttons stay as they are */ }
    this.disegnaScelti()
  }

  scegliTargetConnected() {
    this.disegnaScelti()
  }

  disegnaScelti() {
    this.scegliTargets.forEach((b) => {
      const fatto = (this.scelti || []).includes(b.dataset.bando)
      b.disabled = fatto || b.dataset.busy === "1"
      b.querySelector("[data-etichetta]").textContent = fatto ? "Scelto per il progetto" : "Scegli per il progetto"
    })
  }

  async scegli(event) {
    const b = event.currentTarget
    const pid = this.profile.project_id
    b.dataset.busy = "1"
    b.disabled = true
    try {
      await apiJson(`/api/projects/${encodeURIComponent(pid)}/bandi-scelti/${encodeURIComponent(b.dataset.bando)}`, { method: "POST" })
      window.toast?.success(`${b.dataset.nome}: scelto per il progetto`)
    } catch (e) {
      window.toast?.error(formatApiErrorDetail(e.detail))
    } finally {
      await this.ricaricaScelti()
      delete b.dataset.busy
      this.disegnaScelti()
    }
  }

  async approfondisci(event) {
    const b = event.currentTarget
    const pid = this.profile.project_id
    b.disabled = true
    try {
      await apiJson(`/api/projects/${encodeURIComponent(pid)}/approfondisci/${encodeURIComponent(b.dataset.bando)}`, { method: "POST" })
      window.toast?.success(`${b.dataset.nome}: in approfondimento, fuori dallo scenario`)
    } catch (e) {
      window.toast?.error(formatApiErrorDetail(e.detail))
    } finally {
      b.disabled = false
    }
  }

  // ---- CreaProgetto senza un progetto ------------------------------------------------------------------------------

  spunta(event) {
    const id = event.currentTarget.dataset.bando
    this.spuntati = this.spuntati.includes(id) ? this.spuntati.filter((x) => x !== id) : [...this.spuntati, id]
    this.disegnaCrea()
  }

  nomeCambiato() {
    this.disegnaCrea()
  }

  disegnaCrea() {
    if (!this.hasCreaTarget) return
    this.contoSpuntatiTarget.textContent = String(this.spuntati.length)
    this.approfondisciRigaTarget.hidden = this.spuntati.length <= 1
    const nomi = Object.fromEntries(this.spuntaTargets.map((s) => [s.dataset.bando, s.dataset.nome]))
    const sel = this.approfondisciTarget
    const scelto = sel.value
    const primo = sel.options[0]
    sel.replaceChildren(primo, ...this.spuntati.map((g) => {
      const o = document.createElement("option")
      o.value = g
      o.textContent = nomi[g] || g
      return o
    }))
    sel.value = this.spuntati.includes(scelto) ? scelto : ""
    this.creaTarget.disabled = this.busy || !this.nomeTarget.value.trim()
  }

  async creaProgetto(event) {
    event.preventDefault()
    if (this.busy) return
    this.busy = true
    this.disegnaCrea()
    try {
      const payload = { ...this.profile, budgetStimato: this.profile.budgetStimato ? Number(this.profile.budgetStimato) : null }
      const approfondisci = this.approfondisciTarget.value
      const data = await apiJson("/api/projects/da-ricerca", { method: "POST", body: { name: this.nomeTarget.value, profilo: payload,
        bandi: this.spuntati, estratto: this.estratto, approfondisci: this.spuntati.includes(approfondisci) ? approfondisci : "" } })
      window.toast?.success("Progetto creato: ecco il riepilogo")
      window.Turbo?.visit(`/app/progetto/${data.id}`)
    } catch (e) {
      window.toast?.error(formatApiErrorDetail(e.detail))
    } finally {
      this.busy = false
      if (this.hasCreaTarget) this.disegnaCrea()
    }
  }
}
